import { useEffect, useMemo, useState } from 'react';

import qs from 'qs';

import { WasmUnavailableError, appendShareId, generateShareId } from '@change-corgi/core/longlinks';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useCurrentUserIdAsync, useSessionUuidAsync } from '#src/app/shared/hooks/session';
import type { ShortUrlInfo } from '#src/app/shared/hooks/shortUrls';
import { useShortUrlsInfo } from '#src/app/shared/hooks/shortUrls';
import { isLoaded } from '#src/app/shared/utils/async';

function getShareUrl(
	{
		sessionUuid,
		currentUserId,
		slug,
		socialNetworkName,
	}: {
		sessionUuid: string | undefined;
		currentUserId: string | undefined;
		slug: string;
		socialNetworkName: string;
	},
	{ resolveUrl }: UtilityContext,
) {
	const recruiterTrackParams = !currentUserId ? {} : { recruiter: currentUserId };
	const recruitedByTrackParams = !sessionUuid ? {} : { recruited_by_id: sessionUuid };
	const trackParams = qs.stringify({
		...recruiterTrackParams,
		...recruitedByTrackParams,
		utm_source: 'movement_share',
		utm_medium: socialNetworkName,
		utm_campaign: socialNetworkName,
	});
	return resolveUrl(`/m/${slug}?${trackParams}`);
}

type Social = 'facebook' | 'twitter' | 'email' | 'whatsapp';

const SOCIALS: readonly Social[] = ['facebook', 'twitter', 'email', 'whatsapp'];

const DEFAULT_TRACKING_PROPERTIES = {
	generated_share_id: '',
	generated_share_id_source: 'generated_before_response',
} as const;

export function useShareUrl({ slug }: { slug: string }): Record<Social, ShortUrlInfo> {
	const utilityContext = useUtilityContext();
	const sessionUuid = useSessionUuidAsync();
	const currentUserId = useCurrentUserIdAsync();

	// `useSessionUuidAsync`/`useCurrentUserIdAsync` map through `useMappedLoadedState`,
	// which returns a fresh object on every render. Memoize against the resolved
	// primitive values instead — otherwise `urls` (and the fallback effect below
	// that depends on it) would change identity every render and loop forever.
	const sessionUuidValue = isLoaded(sessionUuid) ? sessionUuid.value : undefined;
	const currentUserIdValue = isLoaded(currentUserId) ? currentUserId.value : undefined;

	const urlArgs = useMemo(
		() => ({
			sessionUuid: sessionUuidValue,
			currentUserId: currentUserIdValue,
			slug,
		}),
		[sessionUuidValue, currentUserIdValue, slug],
	);

	const loaded = isLoaded(sessionUuid) && isLoaded(currentUserId);

	const urls: Record<Social, string> = useMemo(
		() => ({
			facebook: getShareUrl({ ...urlArgs, socialNetworkName: 'facebook' }, utilityContext),
			twitter: getShareUrl({ ...urlArgs, socialNetworkName: 'twitter' }, utilityContext),
			email: getShareUrl({ ...urlArgs, socialNetworkName: 'email' }, utilityContext),
			whatsapp: getShareUrl({ ...urlArgs, socialNetworkName: 'whatsapp' }, utilityContext),
		}),
		[urlArgs, utilityContext],
	);

	// Don't queue lambda calls until session + user are loaded — the share URL
	// incorporates `recruiter` and `recruited_by_id` UTMs that come from those.
	const shortUrlsInfo: Partial<Record<Social, ShortUrlInfo>> = useShortUrlsInfo(loaded ? urls : {});

	// `generateShareId` is async (xxhash-wasm), so we compute the local fallback
	// off-render and stash the result. Until it resolves, early clicks fall back
	// to the bare URL with an empty share_id, same first-render contract as
	// `useShortUrlsInfo` itself.
	const [fallbackInfos, setFallbackInfos] = useState<Partial<Record<Social, ShortUrlInfo>>>({});
	useEffect(() => {
		let cancelled = false;
		void (async () => {
			const entries = (
				await Promise.all(
					SOCIALS.map(async (social) => {
						const url = urls[social];
						try {
							const generatedShareId = await generateShareId(url);
							return [
								social,
								{
									url: appendShareId(url, generatedShareId),
									trackingProperties: {
										generated_share_id: generatedShareId,
										generated_share_id_source: 'generated_before_response' as const,
									},
								},
							] as const;
						} catch (err) {
							if (err instanceof WasmUnavailableError) {
								// Expected: iOS Lockdown Mode / in-app webview without WebAssembly.
								// Fall back to the bare URL — the caller's defaultFallback handles it.
								return null;
							}
							throw err;
						}
					}),
				)
			).filter((entry) => entry !== null);
			// oxlint-disable-next-line typescript/no-unnecessary-condition
			if (cancelled) return;
			// oxlint-disable-next-line typescript/no-unsafe-type-assertion
			setFallbackInfos(Object.fromEntries(entries) as Record<Social, ShortUrlInfo>);
		})();
		return () => {
			cancelled = true;
		};
	}, [urls]);

	return useMemo(() => {
		const defaultFallback = (url: string): ShortUrlInfo => ({
			url,
			trackingProperties: DEFAULT_TRACKING_PROPERTIES,
		});
		const fallback = (social: Social): ShortUrlInfo => fallbackInfos[social] ?? defaultFallback(urls[social]);
		return {
			facebook: shortUrlsInfo.facebook ?? fallback('facebook'),
			twitter: shortUrlsInfo.twitter ?? fallback('twitter'),
			email: shortUrlsInfo.email ?? fallback('email'),
			whatsapp: shortUrlsInfo.whatsapp ?? fallback('whatsapp'),
		};
	}, [urls, shortUrlsInfo, fallbackInfos]);
}
